import { Transaction } from "../types";

// Convert CSV string to array of Transaction objects
export function parseCSVToTransactions(
  csvText: string
): Omit<Transaction, "id">[] {
  const lines = csvText.split("\n");
  if (lines.length < 2) {
    throw new Error("CSV file is empty or invalid");
  }

  // Parse headers
  const headers = lines[0].split(",").map((header) => header.trim());

  // Validate required headers
  const requiredHeaders = ["description", "amount", "type", "category", "date"];
  const missingHeaders = requiredHeaders.filter(
    (header) => !headers.includes(header)
  );

  if (missingHeaders.length > 0) {
    throw new Error(`Missing required headers: ${missingHeaders.join(", ")}`);
  }

  const transactions: Omit<Transaction, "id">[] = [];

  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue; // Skip empty lines

    // Handle CSV with quoted values containing commas
    const values: string[] = [];
    let currentValue = "";
    let inQuotes = false;

    for (let j = 0; j < line.length; j++) {
      const char = line[j];

      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === "," && !inQuotes) {
        values.push(currentValue.trim());
        currentValue = "";
      } else {
        currentValue += char;
      }
    }
    values.push(currentValue.trim());

    // Create transaction object
    const transaction: any = {};

    headers.forEach((header, index) => {
      if (index < values.length) {
        transaction[header] = values[index];
      }
    });

    // Validate and convert transaction
    try {
      const validatedTransaction: Omit<Transaction, "id"> = {
        description: String(transaction.description || "").trim(),
        amount: Math.abs(parseFloat(transaction.amount) || 0),
        type: transaction.type === "income" ? "income" : "expense",
        category: String(transaction.category || "Other").trim(),
        date: parseDate(transaction.date),
        paymentMethod: transaction.paymentMethod || "Cash",
        tags: transaction.tags
          ? transaction.tags.split(";").map((tag: string) => tag.trim())
          : [],
      };

      // Validate required fields
      if (!validatedTransaction.description) {
        throw new Error(`Missing description in row ${i + 1}`);
      }

      if (validatedTransaction.amount <= 0) {
        throw new Error(`Invalid amount in row ${i + 1}`);
      }

      transactions.push(validatedTransaction);
    } catch (error) {
      console.warn(`Skipping invalid row ${i + 1}:`, error);
    }
  }

  return transactions;
}

// Parse date string to YYYY-MM-DD format
function parseDate(dateString: string): string {
  if (!dateString) {
    return new Date().toISOString().split("T")[0];
  }

  // Try different date formats
  try {
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      return date.toISOString().split("T")[0];
    }
  } catch (error) {
    // Try parsing common formats
    if (dateString.includes("/")) {
      const parts = dateString.split("/");
      if (parts.length === 3) {
        // MM/DD/YYYY or DD/MM/YYYY
        const month = parseInt(parts[0], 10);
        const day = parseInt(parts[1], 10);
        const year = parseInt(parts[2], 10);
        const date = new Date(year, month - 1, day);
        if (!isNaN(date.getTime())) {
          return date.toISOString().split("T")[0];
        }
      }
    } else if (dateString.includes("-")) {
      const parts = dateString.split("-");
      if (parts.length === 3) {
        // YYYY-MM-DD or DD-MM-YYYY
        if (parts[0].length === 4) {
          // YYYY-MM-DD
          return dateString;
        } else {
          // DD-MM-YYYY
          const day = parseInt(parts[0], 10);
          const month = parseInt(parts[1], 10);
          const year = parseInt(parts[2], 10);
          const date = new Date(year, month - 1, day);
          if (!isNaN(date.getTime())) {
            return date.toISOString().split("T")[0];
          }
        }
      }
    }
  }

  // If all parsing fails, return today's date
  return new Date().toISOString().split("T")[0];
}

// Convert transactions to CSV string
export function convertTransactionsToCSV(transactions: Transaction[]): string {
  if (transactions.length === 0) {
    return "";
  }

  const headers = [
    "description",
    "amount",
    "type",
    "category",
    "date",
    "paymentMethod",
    "tags",
  ];

  const csvRows = [
    headers.join(","),
    ...transactions.map((transaction) => {
      const values = [
        `"${transaction.description.replace(/"/g, '""')}"`,
        transaction.amount,
        transaction.type,
        transaction.category,
        transaction.date,
        transaction.paymentMethod || "Cash",
        transaction.tags ? transaction.tags.join(";") : "",
      ];
      return values.join(",");
    }),
  ];

  return csvRows.join("\n");
}

// Generate CSV template for users
export function generateCSVTemplate(): string {
  const headers = [
    "description",
    "amount",
    "type",
    "category",
    "date",
    "paymentMethod",
    "tags",
  ];
  const examples = [
    [
      "Salary Deposit",
      "5000",
      "income",
      "Salary",
      "2024-01-15",
      "Bank Transfer",
      "monthly;salary",
    ],
    [
      "Groceries",
      "150.50",
      "expense",
      "Food & Dining",
      "2024-01-16",
      "Credit Card",
      "grocery;weekly",
    ],
    [
      "Netflix Subscription",
      "15.99",
      "expense",
      "Entertainment",
      "2024-01-17",
      "Credit Card",
      "subscription",
    ],
    [
      "Freelance Work",
      "800",
      "income",
      "Freelance",
      "2024-01-18",
      "Bank Transfer",
      "freelance;project",
    ],
    [
      "Gas",
      "45.75",
      "expense",
      "Transportation",
      "2024-01-19",
      "Debit Card",
      "car;fuel",
    ],
  ];

  const csvRows = [
    headers.join(","),
    ...examples.map((row) => {
      return row.map((cell) => `"${cell}"`).join(",");
    }),
    ["Coffee", "5.25", "expense", "Food & Dining", "2024-01-20", "Cash", ""]
      .map((cell) => `"${cell}"`)
      .join(","),
  ];

  return csvRows.join("\n");
}
